/**
 * Projects list page
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Checkbox,
  Alert,
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import dayjs from 'dayjs'
import api from '@/services/api'

const { Title } = Typography
const { Search } = Input

interface Project {
  id: number
  crm_id: number
  project_name: string
  status: string
  total_sources: number
  active_sources: number
  pending_sources: number
  last_sync_at: string | null
  created_at: string
  parse_manual: boolean
  last_swipe_operator_name: string | null
  last_swipe_at: string | null
  vdl_manager_names: string[] | null
}

interface VdlManager {
  id: number
  fio: string
}


const statusColors: Record<string, string> = {
  active: 'success',
  test_active: 'processing',
  pause: 'warning',
  stop: 'error',
  test_stop: 'default',
}

function ProjectsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(false)
  const [managers, setManagers] = useState<VdlManager[]>([])
  const [managersSyncedAt, setManagersSyncedAt] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)

  // * Инициализация из URL параметров
  const initialPage = parseInt(searchParams.get('page') || '1', 10)
  const initialSearch = searchParams.get('search') || ''
  const initialStatus = searchParams.get('status') || undefined
  const initialParseManual = searchParams.get('parse_manual') === 'true'
  const initialVdlManagerId = searchParams.get('vdl_manager_id')
    ? parseInt(searchParams.get('vdl_manager_id')!, 10)
    : undefined

  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  })
  
  const [filters, setFilters] = useState({
    search: initialSearch,
    status: initialStatus as string | undefined,
    parse_manual: initialParseManual,
    vdl_manager_id: initialVdlManagerId as number | undefined,
  })

  const fetchProjects = async (page: number = 1, pageSize: number = 20) => {
    setLoading(true)
    
    try {
      const response = await api.getProjects({
        page,
        page_size: pageSize,
        search: filters.search || undefined,
        status: filters.status,
        parse_manual: filters.parse_manual ? true : undefined,
        vdl_manager_id: filters.vdl_manager_id || undefined,
      })

      setProjects(response.items)
      setManagersSyncedAt(response.managers_synced_at ?? null)
      setPagination((prev) => ({
        ...prev,
        current: response.page,
        pageSize: response.page_size,
        total: response.total,
      }))
    } catch (error: any) {
      console.error('Fetch projects error:', error)
      message.error('Ошибка загрузки проектов')
    } finally {
      setLoading(false)
    }
  }

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    const newPage = newPagination.current || 1
    const newPageSize = newPagination.pageSize || pagination.pageSize
    
    console.log('🔄 ProjectsPage handleTableChange called')
    console.log('  Current pagination:', pagination)
    console.log('  New pagination:', newPagination)
    console.log('  Computed - newPage:', newPage, 'newPageSize:', newPageSize)
    
    // * Update pagination state
    setPagination((prev) => {
      const updated = {
        ...prev,
        current: newPage,
        pageSize: newPageSize,
      }
      console.log('  Updated pagination state:', updated)
      return updated
    })
    
    // * Обновляем URL с новой страницей, сохраняя все существующие параметры
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('page', newPage.toString())
      return params
    })
    
    fetchProjects(newPage, newPageSize)
  }

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }))
    
    // * Обновляем URL
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (value) {
        params.set('search', value)
      } else {
        params.delete('search')
      }
      params.set('page', '1') // Сброс на первую страницу
      return params
    })
  }

  const handleStatusChange = (value: string | undefined) => {
    setFilters((prev) => ({ ...prev, status: value }))
    
    console.log('🎯 handleStatusChange called, value:', value)
    
    // * Обновляем URL
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      console.log('📝 Before:', prev.toString())
      if (value) {
        params.set('status', value)
      } else {
        params.delete('status')
      }
      params.set('page', '1') // Сброс на первую страницу
      console.log('📝 After:', params.toString())
      return params
    })
  }

  const handleParseManualChange = (checked: boolean) => {
    setFilters((prev) => ({ ...prev, parse_manual: checked }))
    
    // * Обновляем URL
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (checked) {
        params.set('parse_manual', 'true')
      } else {
        params.delete('parse_manual')
      }
      params.set('page', '1') // Сброс на первую страницу
      return params
    })
  }

  const handleVdlManagerChange = (value: number | undefined) => {
    setFilters((prev) => ({ ...prev, vdl_manager_id: value }))

    // * Обновляем URL
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (value !== undefined && value !== null) {
        params.set('vdl_manager_id', String(value))
      } else {
        params.delete('vdl_manager_id')
      }
      params.set('page', '1') // Сброс на первую страницу
      return params
    })
  }

  const handleSyncManagers = async () => {
    setSyncing(true)
    try {
      await api.syncVdlManagers()
      message.success('Синхронизация ВДЛщиков запущена, обновите страницу через пару минут')
    } catch (error) {
      console.error('Sync managers error:', error)
      message.error('Не удалось запустить синхронизацию ВДЛщиков')
    } finally {
      setSyncing(false)
    }
  }

  useEffect(() => {
    // * Справочник ВДЛщиков для дропдауна
    api
      .getVdlManagers()
      .then(setManagers)
      .catch((error) => console.error('Fetch VDL managers error:', error))
  }, [])

  useEffect(() => {
    // * При изменении фильтров загружаем проекты с текущей страницей из URL
    const currentPage = parseInt(searchParams.get('page') || '1', 10)
    fetchProjects(currentPage, pagination.pageSize)
  }, [filters.search, filters.status, filters.parse_manual, filters.vdl_manager_id])

  const columns: ColumnsType<Project> = [
    {
      title: 'Проект',
      key: 'project',
      ellipsis: true,
      render: (_: any, record: Project) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 2, fontSize: 14 }}>
            {record.project_name}
          </div>
          <Space size={8} style={{ fontSize: 11, color: '#8c8c8c' }}>
            <span>CRM: {record.crm_id}</span>
            {record.parse_manual && (
              <Tag color="blue" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
                Вебмастер
              </Tag>
            )}
          </Space>
        </div>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'} style={{ fontSize: 11, margin: 0 }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'ВДЛщик',
      key: 'vdl_manager',
      width: 160,
      ellipsis: true,
      render: (_: any, record: Project) =>
        record.vdl_manager_names && record.vdl_manager_names.length > 0 ? (
          <span style={{ fontSize: 12 }}>{record.vdl_manager_names.join(', ')}</span>
        ) : (
          <span style={{ fontSize: 12, color: '#bfbfbf' }}>—</span>
        ),
    },
    {
      title: 'Источники',
      key: 'sources',
      width: 140,
      render: (_: any, record: Project) => (
        <div style={{ fontSize: 12 }}>
          <Space size={8}>
            <span title="Всего" style={{ color: '#1890ff' }}>
              📊 {record.total_sources}
            </span>
            <span title="Активных" style={{ color: '#52c41a' }}>
              ✅ {record.active_sources}
            </span>
            <span title="Ожидание" style={{ color: '#faad14' }}>
              ⏳ {record.pending_sources}
            </span>
          </Space>
        </div>
      ),
    },
    {
      title: 'Последняя синхронизация',
      dataIndex: 'last_sync_at',
      key: 'last_sync_at',
      width: 160,
      render: (date: string | null) =>
        date ? (
          <span style={{ fontSize: 12 }}>{dayjs(date).format('DD.MM.YY HH:mm')}</span>
        ) : (
          <span style={{ fontSize: 12, color: '#bfbfbf' }}>Не синхронизирован</span>
        ),
    },
    {
      title: 'Последний оператор',
      key: 'last_swipe',
      width: 180,
      render: (_: any, record: Project) =>
        record.last_swipe_operator_name ? (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500 }}>{record.last_swipe_operator_name}</div>
            {record.last_swipe_at && (
              <div style={{ color: '#8c8c8c', fontSize: 11 }}>
                {dayjs(record.last_swipe_at).format('DD.MM.YY HH:mm')}
              </div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: 12, color: '#bfbfbf' }}>—</span>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      fixed: 'right' as const,
      render: (_: any, record: Project) => (
        <Button
          type="link"
          icon={<RightOutlined />}
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/projects/${record.crm_id}/sources`)
          }}
          title="Открыть проект"
        />
      ),
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>
            Проекты
          </Title>
          
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchProjects(pagination.current, pagination.pageSize)}
            loading={loading}
          >
            Обновить
          </Button>
        </div>

        <Space size="middle" style={{ width: '100%' }}>
          <Search
            placeholder="Поиск по названию"
            allowClear
            onSearch={handleSearch}
            defaultValue={initialSearch}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
          
          <Select
            placeholder="Статус"
            allowClear
            style={{ width: 200 }}
            value={filters.status}
            onChange={handleStatusChange}
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Test Active', value: 'test_active' },
              { label: 'Pause', value: 'pause' },
              { label: 'Stop', value: 'stop' },
              { label: 'Test Stop', value: 'test_stop' },
            ]}
          />
          
          <Select
            placeholder="ВДЛщик"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
            value={filters.vdl_manager_id}
            onChange={handleVdlManagerChange}
            options={managers.map((m) => ({ label: m.fio, value: m.id }))}
          />

          <Checkbox
            checked={filters.parse_manual}
            onChange={(e) => handleParseManualChange(e.target.checked)}
          >
            Синхронизация с вебмастером
          </Checkbox>
        </Space>

        {filters.vdl_manager_id !== undefined &&
          (managersSyncedAt ? (
            <Alert
              type="info"
              showIcon
              style={{ padding: '6px 12px' }}
              message={`Данные ВДЛщиков на ${dayjs(managersSyncedAt).format('DD.MM.YY HH:mm')}`}
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              style={{ padding: '6px 12px' }}
              message="Список менеджеров не синхронизирован — фильтр может показывать неполный список"
              action={
                <Button size="small" loading={syncing} onClick={handleSyncManagers}>
                  Синхронизировать
                </Button>
              }
            />
          ))}

        <Table
          columns={columns}
          dataSource={projects}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="id"
          size="small"
          onRow={(record) => ({
            style: { cursor: 'pointer' },
            onClick: () => navigate(`/projects/${record.crm_id}/sources`),
          })}
        />
      </Space>
    </div>
  )
}

export default ProjectsPage

